import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { History, Search, Eye, X } from 'lucide-react';

interface SaleHistoryItem {
    id: number;
    total_amount: string | number;
    sale_date: string;
    first_name: string;
    last_name: string;
    role: string;
}

const SalesHistory = () => {
    const [sales, setSales] = useState<SaleHistoryItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Pour le modal de détails
    const [selectedSale, setSelectedSale] = useState<SaleHistoryItem | null>(null);
    const [saleDetails, setSaleDetails] = useState<any[]>([]);
    const [loadingDetails, setLoadingDetails] = useState(false);

    useEffect(() => {
        fetchSalesHistory();
    }, []);

    // Récupérer l'historique des ventes
    const fetchSalesHistory = async () => {
        try {
            setLoading(true);
            const response = await api.get('/pos/sales-history');
            setSales(response.data);
        } catch (error) {
            console.error('Erreur lors du chargement de l\'historique des ventes:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredSales = sales.filter(sale => {
        const fullName = `${sale.first_name} ${sale.last_name}`.toLowerCase();
        return fullName.includes(searchTerm.toLowerCase());
    });

    const openDetailsModal = async (sale: SaleHistoryItem) => {
        setSelectedSale(sale);
        setLoadingDetails(true);
        try {
            const response = await api.get(`/pos/sales-history/${sale.id}`);
            setSaleDetails(response.data);
        } catch (error) {
            console.error('Erreur lors du chargement des détails:', error);
        } finally {
            setLoadingDetails(false);
        }
    };

    const closeDetailsModal = () => {
        setSelectedSale(null);
        setSaleDetails([]);
    };

    return (
        <div className="flex flex-col h-full bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <History className="text-brand-primary" />
                            Historique des Ventes
                        </h1>
                        <p className="text-gray-500 mt-1 text-sm">
                            Consultez l'ensemble des ventes réalisées par les utilisateurs.
                        </p>
                    </div>
                    <div className="relative w-full sm:w-64">
                        <input
                            type="text"
                            placeholder="Rechercher par caissier..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-auto p-6">
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-primary"></div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 text-gray-600 border-b border-gray-200 text-sm uppercase tracking-wider">
                                    <th className="p-4 font-semibold">ID</th>
                                    <th className="p-4 font-semibold">Date</th>
                                    <th className="p-4 font-semibold">Caissier</th>
                                    <th className="p-4 font-semibold">Rôle</th>
                                    <th className="p-4 font-semibold text-right">Montant Total</th>
                                    <th className="p-4 font-semibold text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredSales.map((sale) => (
                                    <tr key={sale.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="p-4 text-sm text-gray-500">#{sale.id}</td>
                                        <td className="p-4 text-sm text-gray-800">
                                            {new Date(sale.sale_date).toLocaleString('fr-FR', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </td>
                                        <td className="p-4 text-sm font-medium text-gray-800">
                                            {sale.first_name} {sale.last_name}
                                        </td>
                                        <td className="p-4 text-sm text-gray-500 capitalize">
                                            {sale.role}
                                        </td>
                                        <td className="p-4 text-sm font-bold text-gray-900 text-right">
                                            {Number(sale.total_amount).toFixed(2)} FCFA
                                        </td>
                                        <td className="p-4 text-center">
                                            <button
                                                onClick={() => openDetailsModal(sale)}
                                                className="inline-flex items-center justify-center p-2 text-brand-primary bg-brand-light rounded hover:bg-brand-primary hover:text-white transition-colors"
                                                title="Voir les détails"
                                            >
                                                <Eye size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {filteredSales.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="p-8 text-center text-gray-500">
                                            Aucune vente trouvée.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal de Détails */}
            {selectedSale && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="flex justify-between items-center p-6 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-gray-800">
                                Détails de la vente #{selectedSale.id}
                            </h2>
                            <button onClick={closeDetailsModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto">
                            <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-600 bg-gray-50 p-4 rounded-md border border-gray-100">
                                <div>
                                    <span className="font-semibold block text-gray-800">Date</span>
                                    {new Date(selectedSale.sale_date).toLocaleString('fr-FR')}
                                </div>
                                <div>
                                    <span className="font-semibold block text-gray-800">Caissier</span>
                                    {selectedSale.first_name} {selectedSale.last_name} ({selectedSale.role})
                                </div>
                            </div>

                            <h3 className="font-bold text-gray-800 mb-4">Articles vendus</h3>

                            {loadingDetails ? (
                                <div className="flex justify-center p-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand-primary"></div>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wider">
                                            <th className="p-3 font-semibold rounded-tl-md">Produit</th>
                                            <th className="p-3 font-semibold">Qté</th>
                                            <th className="p-3 font-semibold text-right">Prix Unitaire</th>
                                            <th className="p-3 font-semibold text-right rounded-tr-md">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {saleDetails.map((item, idx) => (
                                            <tr key={idx}>
                                                <td className="p-3 text-sm text-gray-800 font-medium">
                                                    {item.product_name || 'Produit inconnu'}
                                                    {item.category && <span className="block text-xs text-gray-500 font-normal">{item.category}</span>}
                                                </td>
                                                <td className="p-3 text-sm text-gray-600">{item.quantity}</td>
                                                <td className="p-3 text-sm text-gray-600 text-right">{Number(item.unit_price).toFixed(2)} FCFA</td>
                                                <td className="p-3 text-sm text-gray-900 font-bold text-right">
                                                    {(Number(item.unit_price) * Number(item.quantity)).toFixed(2)} FCFA
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-gray-50">
                                            <td colSpan={3} className="p-4 text-right font-bold text-gray-800">Montant Total :</td>
                                            <td className="p-4 text-right font-bold text-brand-primary text-lg whitespace-nowrap">
                                                {Number(selectedSale.total_amount).toFixed(2)} FCFA
                                            </td>
                                        </tr>
                                    </tfoot>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalesHistory;
